<template>
	<div>
		<!-- Submenu -->
		<client-submenu />

		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline">{{ $t("policies") }}</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5 mt2">
				<j-data-table
					:header="header"
					:list="computedPolicies"
					:sortable="false"
					:isLoading="isLoading"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<!-- link -->
							<template v-if="i === 0">
								<router-link
									:to="decidePolicyLink(item.originalStatus, item.id)"
									>{{ item[column.field] }}</router-link
								>
							</template>
							<!-- status -->
							<template v-else-if="column.field === 'status'">
								<j-tag
									:label="item.status"
									:variant="decideStatusClass(item.originalStatus)"
								/>
							</template>
							<template v-else>{{ item[column.field] }}</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>
		</core-layout-content>
	</div>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import ClientSubmenu from "../components/ClientSubmenu"
import CrmIcon from "@/components/CrmIcon/"
import JHeadline from "@/components/JHeadline/"
import JText from "@/components/JText/"
import JTag from "@/components/JTag/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JAvatar from "@/components/JAvatar/"
import JBaseToggle from "@/components/JBaseToggle/"
import { mapState } from "vuex"
import dayjs from "dayjs"
import get from "lodash.get"
import debounce from "lodash.debounce"
import api from "../api/"

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		ClientSubmenu,
		CrmIcon,
		JText,
		JTag,
		JButton,
		JHeadline,
		JCard,
		JDataTable,
		JAvatar,
		JBaseToggle
	},

	props: {
		id: {
			type: String,
			required: true
		},
		clientType: {
			type: String,
			required: true
		}
	},

	data() {
		return {
			isLoading: false,

			// Fetched data
			policies: [],

			// Search
			searchTerm: null,

			// Filtering
			filter: {
				isFiltering: false,
				payload: null, // payload of filter form
				where: null // processed payload into where param of graphql query
			},
			forceFilterReset: 0
		}
	},

	computed: {
		...mapState("policies", ["listing"]),

		// Header of table
		header() {
			return [
				{
					field: "policyNumber",
					label: this.$t("policy_number")
				},
				{
					field: "insuranceType",
					label: this.$t("type")
				},
				{
					field: "insurerName",
					label: this.$t("insurer")
				},
				{
					field: "productName",
					label: this.$t("product")
				},
				{
					field: "premium",
					label: this.$t("premium")
				},
				{
					label: this.$t("status"),
					field: "status"
				},
				{
					label: this.$t("created"),
					field: "createdAt"
				}
			]
		},

		// What we are showing to user
		computedPolicies() {
			/**
			 * Helper function to format date
			 */
			const formatDate = date =>
				date ? dayjs(date).format(this.$store.state.settings.dateFormat) : null

			/**
			 * Return computed list
			 */
			return this.policies.map(item => ({
				id: item.id,
				policyNumber: item.issuerNumber ? item.issuerNumber : "---",
				startDate: formatDate(item.startDate),
				endDate: formatDate(item.endDate),
				issueDate: formatDate(item.issueDate),
				referrerName: get(item, "referrer.name", ""),
				status: item.status ? this.$t(item.status) : "",
				originalStatus: item.status ? item.status : null,
				insurerName: get(item, "acceptedOffer.product.insurer.name"),
				productName: get(item, "acceptedOffer.product.name"),
				premium: get(item, "acceptedOffer.premium.formattedPrice", ""),
				source: item.source ? this.$t(`sources.${item.source}`) : null,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null,
				// TODO: this is not correct, we should get insuranceType name on product somehow as it's dynamic now
				// API will need to add some fields probably
				insuranceType: item?.acceptedOffer?.product?.productId?.type
					? this.$t(
							`insurance_types.${get(
								item,
								"acceptedOffer.product.productId.type"
							)}`
					  )
					: null
			}))
		}
	},

	methods: {
		async fetchPolicies() {
			// Based on clientType, decide what api we are calling
			// If it's inividual vs company
			let apiKeys = {
				query: "policiesOfIndividual",
				res: "individuals"
			}
			if (this.clientType === "company")
				apiKeys = {
					query: "policiesOfCompany",
					res: "companies"
				}

			const { searchTerm, filter } = this

			// Sort by date
			const sort = { fieldName: "createdAt", type: "desc" }

			// Where
			const where = { id: this.id }

			// Api call
			this.isLoading = true
			const res = await api[apiKeys.query]({ sort, where })
			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			// Success
			this.policies = get(res, `data[${apiKeys.res}].list[0].holderOf`, [])
		},

		/**
		 * When policy is issued, we do link to new modules
		 * When policy is not issued, we do link to old pages
		 * TODO: it's a hack for now, we are changing how policies are displayed
		 * once policies are only issued policies, this should be removed
		 * Always redirect to new pages for AEG and Berkley
		 */
		decidePolicyLink(status, id) {
			if (
				["aeg", "aeg_uat", "berkley_uat", "berkley", "demo_uat"].includes(
					this.$store.state.tenantId
				)
			)
				return `/${this.$store.state.tenantId}/policies/detail/policy/${id}`
			if (status === "ISSUED" || status === "CANCELLED")
				return `/${this.$store.state.tenantId}/policies/detail/policy/${id}`
			else return `/${this.$store.state.tenantId}/quotes/detail/${id}`
		},

		decideStatusClass(status) {
			if (status === "QUOTING" || status === "UNDEFINED") return "warning"
			if (status === "REJECTED" || status === "CANCELLED") return "error"
			if (status === "ISSUED") return "success"
		}
	},

	created() {
		// Check if we have required props id an clientType
		// If not, do nothing
		if (!this.id || !this.clientType) return
		this.fetchPolicies()
	}
}
</script>
